import React, { useState } from "react";
import { Button, Image, Input } from "semantic-ui-react";
import classNames from "classnames";
import { map } from "lodash";
import { toast } from "react-toastify";
import { useOrder, useNow, useAuth } from "../../../../hooks";
import { ModalConfirm } from "../../../Common";
import { ORDER_STATUS, CANCEL_REASONS } from "../../../../utils/constants";
import { formatElapsed } from "../../../../utils/date";
import { getProductionArea } from "../../../../utils/order";
import "./OrderItemAdmin.scss";

export function OrderItemAdmin(props) {
  const { order, onReloadOrders, batchMode = false } = props;
  const { title, image } = order.product_data;
  const { checkDeliveredOrder, deleteOrder } = useOrder();
  const { auth } = useAuth();
  const [showForceDeliver, setShowForceDeliver] = useState(false);
  const [showCancelOrder, setShowCancelOrder] = useState(false);
  const [cancelReason, setCancelReason] = useState(null);
  const [cancelNote, setCancelNote] = useState("");
  const now = useNow();

  // Un plato con área de producción solo se entrega cuando su área lo marca
  // "Listo" (el servidor también lo exige). Solo el Admin puede entregarlo igual.
  const area = getProductionArea(order);
  const waitingForArea = order.status === ORDER_STATUS.PENDING && !!area;
  const isAdmin = !!auth?.me?.is_staff;

  const onCheckDeliveredOrder = async () => {
    setShowForceDeliver(false);
    const result = await checkDeliveredOrder(order.id);
    if (!result.ok) toast.error(result.message);
    onReloadOrders();
  };

  const openCancelOrder = () => {
    setCancelReason(null);
    setCancelNote("");
    setShowCancelOrder(true);
  };

  // El pedido se borra, pero el backend lo registra con el motivo para el
  // reporte "Anulaciones" (control del dueño).
  const onCancelOrder = async () => {
    setShowCancelOrder(false);
    await deleteOrder(order.id, cancelReason, cancelNote);
    onReloadOrders();
  };

  const cancelContent = (
    <div className="order-item-admin__cancel">
      <p>
        Se cancelará <strong>"{title}"</strong>. Esta acción no se puede
        deshacer. ¿Por qué se cancela?
      </p>
      <div className="order-item-admin__reasons">
        {CANCEL_REASONS.map((reason) => (
          <Button
            key={reason.value}
            type="button"
            basic={cancelReason !== reason.value}
            color={cancelReason === reason.value ? "orange" : undefined}
            onClick={() => setCancelReason(reason.value)}
          >
            {reason.text}
          </Button>
        ))}
      </div>
      {cancelReason === "OTHER" && (
        <Input
          fluid
          placeholder="Detalle (opcional)"
          value={cancelNote}
          maxLength={255}
          onChange={(_, data) => setCancelNote(data.value)}
        />
      )}
    </div>
  );

  return (
    <div
      className={classNames("order-item-admin", {
        [order.status.toLowerCase()]: true,
      })}
    >
      {/* Solo mientras espera: una vez entregado, el contador ya no aporta. */}
      {order.status === ORDER_STATUS.PENDING && (
        <div className="order-item-admin__time">
          <span>{formatElapsed(order.created_at, now)}</span>
        </div>
      )}
      {order.status === ORDER_STATUS.READY && (
        <div className="order-item-admin__time order-item-admin__time--ready">
          <span>
            Listo para llevar
            {order.ready_at && ` · ${formatElapsed(order.ready_at, now)}`}
          </span>
        </div>
      )}

      <div className="order-item-admin__product">
        {image && <Image src={image} />}
        <div>
          <p>{title}</p>
          {order.offer_data && (
            <p className="order-item-admin__offer">
              🎁 {order.offer_data.title}
            </p>
          )}
          {order.takeaway && <p className="order-item-admin__takeaway">🥡 Para llevar</p>}
          {order.is_courtesy && (
            <p className="order-item-admin__courtesy">
              Cortesía (S/ 0.00){order.courtesy_reason && ` · ${order.courtesy_reason}`}
            </p>
          )}
          {order.option_choice && (
            <p className="order-item-admin__note">{order.option_choice}</p>
          )}
          {map(order.extras, (extra) => (
            <p className="order-item-admin__extra" key={extra.id}>
              + {extra.label} x{extra.quantity}
            </p>
          ))}
          <p className="order-item-admin__note">
            Registrado por: {order.created_by_name || "Autoservicio (QR)"}
          </p>
          {order.note && (
            <p className="order-item-admin__note">Nota: {order.note}</p>
          )}
        </div>
      </div>

      {order.status !== ORDER_STATUS.DELIVERED && (
        <div className="order-item-admin__actions">
          {/* WhatsApp / Venta rápida / Delivery: se entrega todo junto con
              "Entregar todo" (ListOrderAdmin), no plato por plato. */}
          {!batchMode && (
            <Button
              primary
              disabled={waitingForArea && !isAdmin}
              onClick={waitingForArea ? () => setShowForceDeliver(true) : onCheckDeliveredOrder}
            >
              {waitingForArea && !isAdmin ? `Esperando a ${area}` : "Marcar entregado"}
            </Button>
          )}
          {/* Una vez que cocina lo marcó Listo ya está preparado: no se cancela
              (regla #10 de CLAUDE.md). */}
          {order.status === ORDER_STATUS.PENDING && (
            <Button negative onClick={openCancelOrder}>
              Cancelar
            </Button>
          )}
        </div>
      )}

      <ModalConfirm
        show={showForceDeliver}
        title="Cocina aún no lo marca listo"
        content={`"${title}" sigue pendiente en ${area}. ¿Entregarlo de todos modos?`}
        onClose={() => setShowForceDeliver(false)}
        onCloseText="Volver"
        onConfirm={onCheckDeliveredOrder}
        onConfirmText="Sí, entregar"
      />

      <ModalConfirm
        show={showCancelOrder}
        title="¿Cancelar este pedido?"
        content={cancelContent}
        onClose={() => setShowCancelOrder(false)}
        onCloseText="Volver"
        onConfirm={onCancelOrder}
        onConfirmText="Sí, cancelar pedido"
        confirmDisabled={!cancelReason}
      />
    </div>
  );
}
